import { AppConfig, useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { Box, Grid } from '@ltvco/refresh-lib/theme';
import { searchFormsRecyclingValidation } from '@ltvco/refresh-lib/utils';
import {
  addressSearch,
  propertySearchParams,
  AddressSearchParams,
  SearchReportResponseData,
  useSession,
  useReportRedirect,
  RapidsSearchData,
} from '@ltvco/refresh-lib/v1';
import { useForm, useMutation } from '@ltvco/refresh-lib/vendors';
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';

import { SearchButton } from '../components';
import { SelectInput } from '../SelectInput';
import { SearchProps } from '../utils';

import {
  IntegratedSearchContainer,
  PropertyFormContainer,
  PropertySearchFieldContainer,
} from './components';
import { PropertySearchInput } from './PropertySearchInput';
import {
  PropertyFormData,
  PROPERTY_SEARCH_TYPES,
  searchTypeOptions,
  getPlaceholderTextForPropertySearch,
  RapidsStateRef,
} from './utils';

export const PropertySearch = React.memo(
  ({
    onSearchSuccess,
    isMobile = false,
    placement = 'dashboard',
  }: SearchProps) => {
    const {
      session: { account },
    } = useSession();
    const {
      featureFlags: { propertyReportFlags },
    } = useAppConstantsContext();
    const { redirectToSearchPropertyListingsUrl } = useReportRedirect();
    const { logError, trackEventGA4 } = useContext(AppConfig);

    const [customPlaceholderText, setCustomPlaceholderText] = useState('');
    const selectedPropertyData = useRef<RapidsSearchData | null>(null);
    const rapidsStateRef = useRef<RapidsStateRef>(null);

    const {
      handleSubmit,
      control,
      formState: { errors },
      setValue,
      setError,
      clearErrors,
      watch,
      trigger,
    } = useForm<PropertyFormData>({
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: {
        address: '',
        searchType: PROPERTY_SEARCH_TYPES.ADDRESS_REPORT,
      },
    });

    const propertyQuery = useMutation<
      SearchReportResponseData,
      Error,
      AddressSearchParams
    >({
      mutationFn: addressSearch,
      onSuccess: (data, variables) => {
        onSearchSuccess &&
          onSearchSuccess({
            permalink: data.report.permalink,
            reportType: 'property',
            searchParams: propertySearchParams(variables),
          });
      },
      onError: (error) => {
        logError('Email Search', error);
      },
    });

    const searchTypeValue = watch('searchType');
    const addressValue = watch('address');

    useEffect(() => {
      setCustomPlaceholderText(
        getPlaceholderTextForPropertySearch(searchTypeValue, isMobile)
      );
    }, [searchTypeValue, isMobile]);

    const handleSearchTypeChange = useCallback(
      (value: string) => {
        setValue('searchType', value);
        clearErrors('address');
      },
      [setValue, clearErrors]
    );

    const handleClearAddress = useCallback(() => {
      setValue('address', '');
      clearErrors('address');
      selectedPropertyData.current = null;
      rapidsStateRef.current?.resetValue();
    }, [setValue, clearErrors]);

    const handlePropertyDataSelected = useCallback(
      (data: RapidsSearchData) => {
        selectedPropertyData.current = data;
      },
      [setValue]
    );

    const handleAddressReportSearch = useCallback(
      (propertyData: RapidsSearchData) => {
        const { street_line, city, state, zipcode } = propertyData;

        const parsedAddress = {
          address: street_line,
          city,
          state,
          zipcode,
          report_flags: JSON.stringify(propertyReportFlags) || '',
        };

        const address = `${street_line}, ${city}, ${state}, ${zipcode}`;
        setValue('address', address);

        propertyQuery.mutate(parsedAddress);
      },
      [propertyQuery, propertyReportFlags]
    );

    const handlePreForeclosuresReportSearch = useCallback(
      (propertyData: RapidsSearchData) => {
        const { city, state, zipcode } = propertyData;
        const personByAddressSearchParams: {
          city?: string;
          state?: string;
          zip5?: string;
        } = {};

        let address = '';

        if (city && state) {
          personByAddressSearchParams.city = city;
          personByAddressSearchParams.state = state;
          address = `${city}, ${state}`;
        }

        if (zipcode) {
          personByAddressSearchParams.zip5 = zipcode;
          address = `${city}, ${state} ${zipcode}`;
        }

        setValue('address', address);

        redirectToSearchPropertyListingsUrl(personByAddressSearchParams);
      },
      [propertyReportFlags, setValue]
    );

    const onPropertySearchSubmit = () => {
      if (!searchFormsRecyclingValidation(account)) {
        return;
      }
      const optionSelected = selectedPropertyData.current;

      if (!optionSelected) {
        const currentInputText = rapidsStateRef.current?.getValue();
        let message = 'Please try searching for a valid street address.';

        if (searchTypeValue === PROPERTY_SEARCH_TYPES.PRE_FORECLOSURES) {
          message = 'Please try searching for a valid city, state or zip code.';
        }

        setValue('address', currentInputText || '');
        setError('address', { message });
        return;
      }

      const searchBy =
        searchTypeValue === PROPERTY_SEARCH_TYPES.ADDRESS_REPORT
          ? 'address'
          : 'pre_foreclosure';

      if (searchTypeValue === PROPERTY_SEARCH_TYPES.ADDRESS_REPORT) {
        handleAddressReportSearch(optionSelected);
      } else if (searchTypeValue === PROPERTY_SEARCH_TYPES.PRE_FORECLOSURES) {
        handlePreForeclosuresReportSearch(optionSelected);
      }

      trackEventGA4({
        event_name: 'report_search',
        report_type: 'property',
        search_by: searchBy,
        search_from: `${placement}_search`,
      });
    };

    const SearchInputComponent = () => {
      return (
        <PropertySearchInput
          placeholder={customPlaceholderText}
          errors={errors}
          clearErrors={clearErrors as () => void}
          onClear={handleClearAddress}
          trigger={trigger}
          onSubmit={handleSubmit(onPropertySearchSubmit)}
          onPropertyDataSelected={handlePropertyDataSelected}
          searchType={searchTypeValue}
          value={addressValue}
          setValue={setValue}
          rapidsStateRef={rapidsStateRef}
        />
      );
    };

    const SelectInputComponent = () => {
      return (
        <SelectInput
          options={searchTypeOptions}
          value={searchTypeValue}
          onChange={handleSearchTypeChange}
          name="searchType"
          control={control}
        />
      );
    };

    const renderMobileForm = useCallback(() => {
      return (
        <Grid container sx={{ mt: 2.5, mb: 2.5 }} gap={3}>
          <PropertySearchFieldContainer>
            <SelectInputComponent />
          </PropertySearchFieldContainer>

          <PropertySearchFieldContainer
            className={errors.address ? 'error' : ''}
          >
            <SearchInputComponent />
          </PropertySearchFieldContainer>

          <SearchButton type="submit" fullWidth>
            Search
          </SearchButton>
        </Grid>
      );
    }, [
      searchTypeValue,
      errors,
      customPlaceholderText,
      clearErrors,
      handleClearAddress,
      handlePropertyDataSelected,
      addressValue,
      setValue,
    ]);

    const renderDesktopForm = useCallback(() => {
      return (
        <PropertyFormContainer>
          <Box flexGrow={1}>
            <PropertySearchFieldContainer
              container
              className={errors.address ? 'error' : ''}
            >
              <Grid
                size={{ xs: 12 }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                <IntegratedSearchContainer>
                  <Box>
                    <SelectInputComponent />
                  </Box>

                  <SearchInputComponent />
                </IntegratedSearchContainer>
              </Grid>
            </PropertySearchFieldContainer>
          </Box>
          <SearchButton type="submit">Search</SearchButton>
        </PropertyFormContainer>
      );
    }, [
      searchTypeValue,
      errors,
      customPlaceholderText,
      clearErrors,
      handleClearAddress,
      handlePropertyDataSelected,
      addressValue,
      setValue,
    ]);

    return (
      <form onSubmit={handleSubmit(onPropertySearchSubmit)}>
        {isMobile ? renderMobileForm() : renderDesktopForm()}
      </form>
    );
  }
);
